import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import apiService from '../../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

interface Invitation {
  id: number;
  invited_email: string;
  invited_by_username: string;
  expires_at: string;
  used_at: string | null;
  used_by_username: string | null;
  created_at: string;
}



function InvitationList() {
  const timezone = useOrgTimezone();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchInvitations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.get<Invitation[]>('/auth/invitations');
      
      if (response.success && response.data) {
        setInvitations(response.data);
      } else {
        throw new Error('Failed to fetch invitations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const formatDate = (dateString: string) => {
    return formatDateTimeInTz(dateString, timezone);
  };

  const getStatusBadge = (invitation: Invitation) => {
    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);
    
    if (invitation.used_at) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✓ Used
        </span>
      );
    } else if (now > expiresAt) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          ⏰ Expired
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          📧 Pending
        </span>
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <button
            onClick={() => setIsExpanded(prev => !prev)}
            className="flex items-center gap-2 text-left hover:text-blue-700 transition-colors"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Recent Invitations
          </button>
          {isExpanded && (
            <button
              onClick={fetchInvitations}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              disabled={isLoading}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          )}
        </CardTitle>
      </CardHeader>
      {isExpanded && <CardContent>
        {isLoading ? (
          <div className="text-center py-8">
            <svg className="animate-spin h-8 w-8 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-500">Loading invitations...</p>
          </div>
        ) : null}
        {!isLoading && error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 mb-4">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!isLoading && invitations.length === 0 ? (
          <div className="text-center py-8">
            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10.5 3L12 2l1.5 1H21v1H3v-1h7.5z" />
            </svg>
            <p className="text-gray-500">No invitations sent yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Use the form above to invite your first user
            </p>
          </div>
        ) : !isLoading ? (
          <div className="space-y-3">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{invitation.invited_email}</p>
                      <p className="text-sm text-gray-500">
                        Invited by {invitation.invited_by_username} • {formatDate(invitation.created_at)}
                      </p>
                    </div>
                    {getStatusBadge(invitation)}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {invitation.used_at ? (
                      <span>Used by {invitation.used_by_username} on {formatDate(invitation.used_at)}</span>
                    ) : (
                      <span>Expires: {formatDate(invitation.expires_at)}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>}
    </Card>
  );
}

export default InvitationList;